import { useState } from "react";
import type { SessionInfo } from "@/lib/manifest/client";

export interface AccountBadgeProps {
  session: SessionInfo;
  onSignedOut: () => void;
}

/**
 * Sign-in control.
 *
 * Signing in is a full-page redirect to Bungie rather than a fetch — OAuth needs
 * the browser to actually navigate, and Bungie's authorize page cannot be framed
 * or XHR'd.
 */
export function AccountBadge({ session, onSignedOut }: AccountBadgeProps) {
  const [busy, setBusy] = useState(false);

  if (!session.signedIn) {
    return (
      <a
        href="/auth/bungie/start"
        className="label-caps border border-line-dim px-2 py-0.5 text-ink-2 transition-colors hover:bg-white/10 hover:text-ink"
      >
        Sign in with Bungie
      </a>
    );
  }

  async function signOut() {
    setBusy(true);
    try {
      await fetch("/auth/logout", { method: "POST" });
    } catch {
      // Even if the request fails the cookie may already be gone; re-checking
      // the session is the honest way to find out.
    }
    setBusy(false);
    onSignedOut();
  }

  return (
    <>
      <span className="label-caps text-ink-2" title="Signed in with Bungie">
        {session.displayName || "Signed in"}
      </span>
      <button
        type="button"
        onClick={signOut}
        disabled={busy}
        className="label-caps border border-line-dim px-2 py-0.5 text-ink-2 transition-colors hover:bg-white/10 hover:text-ink disabled:opacity-50"
      >
        {busy ? "…" : "Sign out"}
      </button>
    </>
  );
}
